import {
  Box,
  Center,
  Flex,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/layout";
import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Switch,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { TaskSystemStatusEnum } from "@src/__generated__/graphql";
import { TaskListingTableHeader } from "@src/components/modules/resource-planning/tasks/listing/components/table-header";
import {
  TASK_ROW_HEIGHT,
  TaskRow,
} from "@src/components/modules/resource-planning/tasks/listing/components/task-row";
import {
  useTasksFiltersInit,
  useTasksFiltersOptions,
} from "@src/components/modules/resource-planning/tasks/listing/hooks";
import { Card } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { NAV_HEIGHT } from "@src/components/widgets/AppSubNav/AppSubNav";
import { BulkActions } from "@src/components/widgets/bulk-actions";
import { ContentContainer } from "@src/components/widgets/Layout/ContentContainer";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { FiltersPanelSearch } from "@src/utils/components/filters/components/filters-panel-search";
import { withCan } from "@src/utils/components/permissions";
import { useStore, useUpdateURI } from "@src/utils/hooks";
import useIntersectionObserver from "@src/utils/hooks/useIntersectionObserver";
import { observer } from "mobx-react-lite";
import { type ReactElement, useEffect, useRef } from "react";
import { TasksBulkActionsModal } from "./components/bulk-actions-modal";

export type TTaskPageVariant =
  | "all"
  | "my"
  | "my-created"
  | "starred"
  | "pinned"
  | "project"
  | "my-watched";

type TaskListProps = {
  variant: TTaskPageVariant;
  navigation?: ReactElement;
};
export const TaskList = withCan<TaskListProps>(
  { do: ["task_read_all", "task_read_own"] },
  observer(function NewTaskList({ variant, navigation }) {
    const { tasksListingStore: store, authStore } = useStore();
    const tasksLoaderRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      store.resetPagination();
    }, [variant]);

    useTasksFiltersInit(variant);
    useTasksFiltersOptions();
    useUpdateURI(
      store.filtersStore.searchParams,
      variant === "project" ? "id" : undefined,
    );

    useIntersectionObserver({
      target: tasksLoaderRef,
      threshold: 0.1,
      onIntersect: () => {
        store.fetchTasks();
      },
    });

    return (
      <Box
        mx={variant === "project" ? "0" : "8"}
        py={variant === "project" ? "10" : "8"}
        h={`calc(100dvh - ${NAV_HEIGHT}px)`}
      >
        <Card h="full" display="flex" flexDirection="column" overflow="hidden">
          <Flex flexDir="column" h="full">
            <Box px="4" borderBottom="1px" borderBottomColor="gray.200">
              {navigation}
            </Box>

            <Box p="4">
              <FiltersPanelRoot filters={store.filtersStore.where}>
                <Stack flex="1" spacing="2">
                  <HStack spacing="3" flex="1">
                    {authStore.isInternalUser && (
                      <FiltersPanelSearch
                        w="full"
                        inputWidth="full"
                        defaultValue={store.filtersStore.searchTerm}
                        searchPlaceholder={
                          variant === "project"
                            ? t`Search by tasks, subtasks, ID, budget item...`
                            : t`Search by projects, tasks`
                        }
                        onSearch={store.filtersStore.handleSearchChange}
                      />
                    )}

                    {!store.filtersStore.showSubtasksAsTasks &&
                      authStore.isInternalUser && (
                        <Button
                          minW="fit-content"
                          colorScheme="grey"
                          leftIcon={<Icon name="git-branch-01" />}
                          onClick={store.toggleSubtasksOpen}
                          size="sm"
                          variant="ghost"
                        >
                          {store.subtasksOpen ? (
                            <Trans>Hide all subtasks</Trans>
                          ) : (
                            <Trans>Show all subtasks</Trans>
                          )}
                        </Button>
                      )}

                    {authStore.isInternalUser && (
                      <FiltersPanelFilters menuListZIndex="5" />
                    )}

                    <FormControl
                      alignItems="center"
                      display="flex"
                      w="fit-content"
                      h="32px"
                    >
                      <FormLabel mb="0">
                        <Trans>Show</Trans>&nbsp;
                        {TaskSystemStatusEnum.Done.toLowerCase()}
                        &nbsp;
                        <Trans>tasks</Trans>
                      </FormLabel>
                      <Switch
                        isChecked={!store.filtersStore.hideDoneTasks}
                        onChange={(e) => {
                          e.stopPropagation();
                          store.filtersStore.toggleDoneTasksFilter();
                        }}
                      />
                    </FormControl>

                    <Popover placement="bottom-end">
                      <PopoverTrigger>
                        <IconButton
                          ml="2"
                          aria-label={t`Columns`}
                          colorScheme="grey"
                          icon={<Icon name="settings-01" />}
                          variant="ghost"
                        />
                      </PopoverTrigger>

                      <PopoverContent w="fit-content">
                        <PopoverBody>
                          <VStack align="flex-start">
                            {store.columnsStore.columnsToControl.map(
                              (column) => (
                                <Checkbox
                                  key={`column-control-${column.key}`}
                                  fontWeight="normal"
                                  isChecked={column.isVisible}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    store.columnsStore.toggleColumnVisibility(
                                      column.key,
                                      e.target.checked,
                                    );
                                  }}
                                >
                                  {column.title}
                                </Checkbox>
                              ),
                            )}
                            <Button
                              mt="4"
                              colorScheme="red"
                              onClick={store.columnsStore.handleResetColumns}
                              size="sm"
                              variant="ghost"
                            >
                              <Trans>Reset columns</Trans>
                            </Button>
                          </VStack>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </HStack>
                </Stack>
              </FiltersPanelRoot>
            </Box>

            <Flex w="full" flex="1" overflow="hidden">
              <Box
                w="full"
                overflow="auto"
                position="relative"
                onScroll={store.handleScroll}
                borderTopColor="gray.200"
                borderTopWidth="1px"
              >
                <TaskListingTableHeader />

                {store.taskCount >= 0 && (
                  <Box minH="fit-content">
                    {store.tasks.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        taskColumns={store.columnsStore.columnsToRender}
                        showParentTaskButton={
                          store.filtersStore.showSubtasksAsTasks
                        }
                      />
                    ))}
                  </Box>
                )}

                {store.filtersStore.pagination.canLoadMore ? (
                  <Box
                    h={TASK_ROW_HEIGHT}
                    bg="white"
                    position="sticky"
                    left="0"
                    w="full"
                  >
                    <Center ref={tasksLoaderRef} h={TASK_ROW_HEIGHT}>
                      <Spinner />
                    </Center>
                  </Box>
                ) : undefined}

                {!store.filtersStore.pagination.canLoadMore &&
                  !store.taskCount &&
                  !store.isFetching && (
                    <Box w="full" bg="white" pos="sticky" left="0">
                      <ContentContainer py="4">
                        <Text fontWeight="medium" textAlign="center">
                          <Trans>
                            No matching results found for the given search
                            criteria.
                          </Trans>
                        </Text>
                      </ContentContainer>
                    </Box>
                  )}
              </Box>
            </Flex>

            <BulkActions store={store.bulkActionsStore} />
            <TasksBulkActionsModal store={store.bulkActionsStore} />
          </Flex>
        </Card>
      </Box>
    );
  }),
);
